import { Layout } from '../components/Layout'
import { RateForm } from '../components/RateForm'
import { PageMeta } from '../lib/meta'
import { surface } from '../surface'

const BANDS: [string, string][] = [
  ['MIN', 'Minimum charge'],
  ['N', 'Normal (under 45kg)'],
  ['+45', '45 kg and up'],
  ['+100', '100 kg and up'],
  ['+300', '300 kg and up'],
  ['+1000', '1000 kg and up'],
]

export function AirratesHome() {
  return (
    <Layout onDark>
      <PageMeta />
      {/* ── Deep-ink hero island ─────────────────────────────────────────────── */}
      <div className="tone-dark overflow-x-clip">
        <section className="hero-glow">
          <div className="mx-auto max-w-3xl px-5 pt-14 pb-16 text-center">
            <span className="eyebrow reveal reveal-1 justify-center">
              <span className="live-dot pulse-dot" aria-hidden />
              {surface.eyebrow}
            </span>
            <h1 className="reveal reveal-2 mx-auto mt-5 max-w-2xl text-[clamp(2.1rem,5.2vw,3.3rem)] font-light leading-[1.02] tracking-[-0.03em] text-ink opacity-95">
              Price any lane, <span className="hl">all-in per kg</span>.
            </h1>
            <p className="reveal reveal-3 mx-auto mt-5 max-w-2xl text-[16px] leading-[1.5] text-muted">
              Origin, destination, weight: the chargeable weight, the weight-band break, fuel and
              security surcharges, and a single all-in per-kg figure. The air-freight rate terminal.
            </p>
            <div className="reveal reveal-3 mx-auto mt-8 max-w-3xl text-left">
              <RateForm />
            </div>
          </div>
        </section>
      </div>

      {/* ── Light content ────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-5 pt-14">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="card p-6">
            <div className="mono text-[11px] uppercase tracking-[0.18em] text-signal">
              Weight bands
            </div>
            <h2 className="mt-1 text-2xl font-light tracking-tight text-ink">
              Rates break by weight
            </h2>
            <p className="mt-2 text-sm text-muted [hyphens:auto] [text-align:justify]">
              Air cargo is quoted per-kg against the chargeable weight, the greater of gross and
              volumetric (volume ÷ 6000). The heavier the shipment, the lower the per-kg break.
            </p>
            <div className="mt-5 space-y-2">
              {BANDS.map(([band, label]) => (
                <div
                  key={band}
                  className="flex items-center gap-3 rounded-xl border border-border bg-surface-1/50 px-3 py-2.5"
                >
                  <span className="mono grid h-7 min-w-[3.5rem] shrink-0 place-items-center rounded-md bg-signal px-2 text-xs font-bold text-on-accent">
                    {band}
                  </span>
                  <span className="text-sm text-ink">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-6">
            <div className="mono text-[11px] uppercase tracking-[0.18em] text-signal">The math</div>
            <h2 className="mt-1 text-2xl font-light tracking-tight text-ink">All-in, not net</h2>
            <p className="mt-2 text-sm text-muted [hyphens:auto] [text-align:justify]">
              Published net rates hide the true cost. We fold fuel (FSC) and security (SSC)
              surcharges into one all-in per-kg number, then charge the greater of that × chargeable
              weight or the lane minimum.
            </p>
            <div className="mono mt-5 space-y-2 rounded-xl border border-border bg-surface-1/50 p-4 text-sm text-ink">
              <div className="flex justify-between">
                <span className="text-muted">chargeable</span>
                <span className="tnum">max(gross, volume ÷ 6000)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">all-in / kg</span>
                <span className="tnum">net + FSC + SSC</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2 font-semibold">
                <span className="text-signal">total</span>
                <span className="tnum text-signal">max(min, chargeable × all-in)</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}
